/*
 *  Copyright (C) 2019 schroeder.org - All Rights Reserved
 *  Unauthorized copying of this file or deriving works therefrom,
 *  via any medium, is strictly prohibited
 *  Proprietary and confidential
 *  <dev@schroeder.org>
 */

// globals
// - dictionary, words with usage stats and history
// - list of words
// - elapsed time in elapsedSeconds
// - hashmap of already seen words, optimization for fast update of display
// - boolean to enable/disable input during audio play
//
// formats:
// dict: {<group>: {
//          <lesson>: {
//            <word>: {
//              status: enabled|disabled|removed,
//              heard: [<timestamp>, ...],
//              correct: [<timestamp>, ...],
//              errorTotal: [<timestamp>, ...],
//              errorMissingArticle: [<timestamp>, ...],
//              errorWrongArticle: [<timestamp>, ...],
//              errorCapitalization: [<timestamp>, ...],
//              errorIncorrect: [<timestamp>, ...]
//            },
//            ... // addl words
//          }
//          ... // addl lessons (lw)
//        }
//        ... // addl groups (diktat)
//      }
//

import Vue from 'vue';
import IDict, {
  DictWordStatus, IDictGroup, IDictLesson, IHistoryDict, IHistoryVersion,
} from '@/types/IDict';
import IWord from '@/types/IWord';
import SubmissionEvent from '@/types/SubmissionEvent';
import WordFilter from '@/utils/WordFilter';

// - original loaded JSON word list
// words.json: { words: { <group>: { <lesson>: [ <word>, ... ], ... } } }

interface ITransitWordGroup {
  [key: string]: { [key: string]: string[] };
}

interface ITransitWordList {
  words: ITransitWordGroup;
}

const VERSION: IHistoryVersion = {
  major: 4,
  minor: 1,
};

export default class WordDictionary {
  public dict: IDict;
  public words: IWord[];

  constructor() {
    this.dict = {};
    this.words = [];
  }

  // initialize
  init(onSuccess: (data: WordDictionary) => void,
       onFailure: (data: WordDictionary, xhr: object) => void): void {
    // load words from JSON doc
    const me = this;
    WordDictionary.loadJSON('words.json',
      (data: ITransitWordList) => {
        me.dict = WordDictionary.parseJsonToDictionary(data.words);
        me.mergeHistory();
        me.words = WordDictionary.shuffleWordList(WordFilter.filterWords(me.dict));
        onSuccess(me);
      },
      (xhr: object) => {
        // eslint-disable-next-line no-console
        Vue.$log('WordDictionary.init fail', xhr);
        onFailure(me, xhr);
      });
  }

  // load local history
  mergeHistory(): void {
    // read previous history from local storage
    const historyDict: IDict = WordDictionary.loadHistory();

    // loop across history, and update loaded dictionary with previous stats.
    // if items in history are not in the current dictionary, copy as removed
    Object.keys(historyDict).forEach((g) => {
      const group = historyDict[g];
      if (!this.dict[g]) {
        this.dict[g] = group;
      }

      Object.keys(group).forEach((l) => {
        const lesson = group[l];
        if (!this.dict[g][l]) {
          this.dict[g][l] = lesson;
        }

        Object.keys(lesson).forEach((word) => {
          const historyDictWord = historyDict[g][l][word];
          if (!this.dict[g][l][word]) {
            this.dict[g][l][word] = { ...historyDictWord, status: DictWordStatus.removed };
          } else {
            this.dict[g][l][word] = { ...historyDictWord };
          }
        });
      });
    });

    WordDictionary.saveHistory(this.dict);
  }

  // add word statistical event
  addCurrentWordEvent(word: IWord, event: SubmissionEvent): void {
    const wordEntry = this.dict[word.group][word.lesson][word.word];
    const now = Date.now();
    wordEntry[event].unshift(now);

    if (`${event}`.match(/^error/)) {
      wordEntry.errorTotal.unshift(now);
    }

    localStorage.setItem('history', JSON.stringify(this.dict));
  }

  static loadHistory(): IDict {
    const historyStr: string = localStorage.getItem('history') || '{}';
    const historyDict: IHistoryDict = JSON.parse(historyStr) || {};

    // could do migrations as appropriate here, but for now, just clear it on mismatch
    const version = historyDict.version || { major: 0, minor: 0 };
    if (version.major < VERSION.major) {
      localStorage.removeItem('history');
      return {};
    }

    return historyDict.dict;
  }

  static saveHistory(dict: IDict): void {
    // save updated dictionary in history
    const historyDict: IHistoryDict = { version: VERSION, dict };
    localStorage.setItem('history', JSON.stringify(historyDict));
    Vue.$log('dictionary saved', historyDict);
  }

  // parse into dictionary list
  static parseJsonToDictionary(jsonWords: ITransitWordGroup): IDict {
    const dict: IDict = {};
    Object.keys(jsonWords).forEach((groupLabel: string) => {
      Object.keys(jsonWords[groupLabel]).forEach((lessonLabel: string) => {
        // top level group
        if (!dict[groupLabel]) {
          dict[groupLabel] = {};
        }
        const group: IDictGroup = dict[groupLabel];

        // subgroup
        if (!group[lessonLabel]) {
          group[lessonLabel] = {};
        }
        const lesson: IDictLesson = group[lessonLabel];

        // word object, stats indexed by word
        jsonWords[groupLabel][lessonLabel].forEach((word: string) => {
          const readyWord = WordDictionary.downcaseArticle(word);
          lesson[readyWord] = {
            hashCode: WordDictionary.hashCode(readyWord),
            status: DictWordStatus.enabled,
            heard: [],
            correct: [],
            errorTotal: [],
            errorMissingArticle: [],
            errorWrongArticle: [],
            errorCapitalization: [],
            errorIncorrect: [],
          };
        });
      });
    });

    return dict;
  }

  // load JSON object from given path
  static loadJSON(path: string,
                  onSuccess: (data: ITransitWordList) => void,
                  onError: (xhr: object) => void): void {
    const xhr = new XMLHttpRequest();
    xhr.overrideMimeType('application/json');
    xhr.onreadystatechange = function () {
      if (xhr.readyState === XMLHttpRequest.DONE) {
        if (xhr.status === 200) {
          if (onSuccess) {
            onSuccess(JSON.parse(xhr.responseText));
          }
        } else if (onError) {
          onError(xhr);
        }
      }
    };

    xhr.open('GET', path, true);
    xhr.send();
  }

  // shuffle (and return) the given array
  static shuffleWordList(wordList: IWord[]): IWord[] {
    for (let i = wordList.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      // eslint-disable-next-line no-param-reassign
      [wordList[i], wordList[j]] = [wordList[j], wordList[i]];
    }
    return wordList;
  }

  // ignore case on articles
  static downcaseArticle(str: string, trim?: boolean): string {
    let result: string = str.replace(/^D(er|ie|as) /, 'd$1 ');
    if (trim) {
      result = result.replace(/^\s+|\s+$/, '');
    }

    return result;
  }

  // generate a hash code for the given string
  static hashCode(str: string): number {
    let hash: number = 0;
    if (!str) {
      return hash;
    }

    for (let i = 0; i < str.length; i += 1) {
      const chr: number = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + chr; // eslint-disable-line no-bitwise
      hash |= 0; // eslint-disable-line no-bitwise
    }

    return hash;
  }
}
